import { Link, router, usePage } from '@inertiajs/react'
import type { PropsWithChildren, ReactNode } from 'react'
import type { MemberUser } from '@/types/serializers'
import { Avatar } from '@/components/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { organization_path, kudos_path, settings_path, account_path, destroy_user_session_path } from '@/routes'

type Nav = {
  organization: { name: string; slug: string }
  can_manage_settings: boolean
  current_user: MemberUser
}

function NavLink({ href, active, children }: { href: string; active: boolean; children: ReactNode }) {
  return (
    <Link
      href={href}
      className={cn(
        'pb-0.5 text-[11px] font-semibold uppercase tracking-widest transition-colors border-b',
        active
          ? 'text-masthead-foreground border-accent'
          // Different opacities per theme, because the two mastheads leave very
          // different room. Light is a mid-tone purple where /60 composites to
          // 3.10:1 — below AA for 11px semibold, on every signed-in page. /85 is
          // the first step that clears it (4.60). Dark is near-black, so /60
          // there is already 6.06 and keeps the stronger de-emphasis.
          //
          // The active link is still distinguishable without relying on that
          // narrowed difference: it carries an accent underline where this one
          // has a transparent border.
          : 'text-masthead-foreground/85 dark:text-masthead-foreground/60 border-transparent hover:text-masthead-foreground'
      )}
    >
      {children}
    </Link>
  )
}

export function OrgLayout({ children }: PropsWithChildren) {
  const { props, url } = usePage<{ nav?: Nav }>()
  const nav = props.nav

  if (!nav) return <>{children}</>

  const { organization, can_manage_settings, current_user } = nav
  const path = url.split('?')[0]

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-masthead border-t-2 border-t-accent shadow-sm">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <Link
            href={organization_path(organization.slug)}
            className="font-serif text-xl font-semibold tracking-tight text-masthead-foreground"
          >
            {organization.name}
          </Link>
          <div className="flex items-center gap-5">
            <nav className="flex items-center gap-5">
              <NavLink href={organization_path(organization.slug)} active={path === organization_path(organization.slug)}>
                Feed
              </NavLink>
              <NavLink href={kudos_path(organization.slug)} active={path === kudos_path(organization.slug)}>
                My Kudos
              </NavLink>
              {can_manage_settings && (
                <NavLink href={settings_path(organization.slug)} active={path === settings_path(organization.slug)}>
                  Settings
                </NavLink>
              )}
            </nav>
            <div className="w-px h-4 bg-masthead-foreground/20" aria-hidden="true" />
            <DropdownMenu>
              <DropdownMenuTrigger className="rounded-full cursor-pointer">
                <Avatar name={current_user.name} avatarUrl={current_user.avatar_url} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel className="font-normal text-muted-foreground">{current_user.name}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => router.visit(account_path())} className="cursor-pointer">
                  Account
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => router.delete(destroy_user_session_path())} className="cursor-pointer">
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>
      {children}
    </div>
  )
}
